import React, { useContext, useEffect, useRef, useState } from 'react';

import { Box, IconButton, styled, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';

import FCList from 'components/FCList';
import ChevronDown from 'components/SvgComponents/icons/ChevronDown';
import ChevronRight from 'components/SvgComponents/icons/ChevronRight';
import ChevronUp from 'components/SvgComponents/icons/ChevronUp';
import ThreeDots from 'components/SvgComponents/icons/ThreeDots';
import { TypographyEllipsis } from 'components/Templates/TypographyEllipsis';
import BreadcrumbRow, {
  ListItem_EntityContainer,
} from 'containers/BusinessOrganisation/components/BreadcrumbRow';
import { OrganizationContext } from 'containers/GlobalWrapper/BusinessOrganizationDataValidation';
import { ProjectContext } from 'containers/GlobalWrapper/ProjectDataValidation';
import { useLazyFetchWorkspaceQuery } from 'redux/api/workspaces.api';
import { Routing } from 'routing/routing';
import { appPaths } from 'utils/constants';

const Div_BreadcrumbContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const IconButton_OrganizationList = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  height: 32,
}));

const ThreeDots_WorkspaceLoading = styled(ThreeDots)({
  width: 32,
});

const IconButton_WorkspaceList = styled(IconButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2),
  height: 32,
  width: 32,
}));

const Box_Chevron = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(-0.5),
}));

const ChevronRight_Custom = styled(ChevronRight)(({ theme }) => ({
  height: 'auto',
  margin: theme.spacing(0, 2),
}));

const ListItem_EntityContainer_Custom = styled(ListItem_EntityContainer)({
  padding: 0,
});

const TypographyEllipsis_Workspace = styled(TypographyEllipsis)({
  maxWidth: 200,
});

const BreadcrumbRow_Container = styled(BreadcrumbRow)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  height: 41,
  borderRadius: theme.shape.borderRadius,
  '& .MuiListItemIcon-root': {
    marginLeft: 0,
  },
  '& .MuiAvatar-root': {
    height: 32,
    width: 32,
  },
  '& .MuiListItemText-root': {
    maxWidth: 200,
  },
}));

export const Breadcrumb = ({ intl, openListOrganisation }) => {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const { organizationId } = useParams();
  const currentOrganization = useContext(OrganizationContext);
  const currentProject = useContext(ProjectContext);
  const [workspaceTree, setWorkspaceTree] = useState([]);
  const [workspaceListIsOpen, setWorkspaceListIsOpen] = useState(false);
  const [workspaceTreeLoading, setWorkspaceTreeLoading] = useState(false);
  const [fetchWorkspace] = useLazyFetchWorkspaceQuery();
  const openWorkspaceClickButton = useRef(null);
  const workspaceRef = useRef(null);

  const fetchWorkspaceRecursively = async workspaceId => {
    const workspace = await fetchWorkspace({ organizationId, workspaceId }).unwrap();
    setWorkspaceTree(prevState => [workspace, ...prevState]);
    if (workspace.parent_id) {
      await fetchWorkspaceRecursively(workspace.parent_id);
    }
  };

  useEffect(
    () => {
      (async () => {
        if (!currentProject?.workspace_id) return;
        setWorkspaceTreeLoading(true);
        await fetchWorkspaceRecursively(currentProject?.workspace_id);
        setWorkspaceTreeLoading(false);
      })();
    },
    [currentProject?.workspace_id],
  );

  const redirectToHome = () => {
    history.push(Routing.home.home(currentOrganization?.id, currentProject?.project_id));
  };

  const redirectToProjecthome = () => {
    history.push(Routing.organizations.projectList(currentOrganization?.id));
  };

  const isOrganizationProjectList = location.pathname.startsWith(appPaths.organizationProjectList);
  return (
    <Div_BreadcrumbContainer>
      <IconButton_OrganizationList variant="text" onClick={openListOrganisation}>
        <Box_Chevron>
          <ChevronUp height={16} width={16} color={theme.palette.background.dark} />
          <ChevronDown height={16} width={16} color={theme.palette.background.dark} />
        </Box_Chevron>
      </IconButton_OrganizationList>
      {location.pathname.includes('/super-admin') && (
        <Typography noWrap variant="h6">
          Quartier Général des Super-Héros
        </Typography>
      )}
      {currentOrganization && (
        <BreadcrumbRow_Container
          entity={currentOrganization}
          intl={intl}
          onClick={redirectToProjecthome}
          showMember
          hideTextInSmallScreen={!isOrganizationProjectList}
        />
      )}
      {currentProject?.workspace_id && (
        <>
          <ChevronRight_Custom height={24} width={24} color={theme.palette.background.dark} />
          {(() => {
            if (workspaceTreeLoading) {
              return (
                <ThreeDots_WorkspaceLoading
                  height={16}
                  width={16}
                  color={theme.palette.background.dark}
                />
              );
            }
            return workspaceTree.length === 1 ? (
              <ListItem_EntityContainer_Custom>
                <BreadcrumbRow_Container
                  entity={workspaceTree[0]}
                  intl={intl}
                  publicId={workspaceTree[0]?.client_reference}
                  hideAvatar
                  isButton={false}
                >
                  {workspaceTree[0]?.name || ''}
                </BreadcrumbRow_Container>
              </ListItem_EntityContainer_Custom>
            ) : (
              <IconButton_WorkspaceList
                variant="text"
                onClick={() => setWorkspaceListIsOpen(!workspaceListIsOpen)}
                ref={openWorkspaceClickButton}
              >
                <ThreeDots height={16} width={16} color={theme.palette.background.dark} />
              </IconButton_WorkspaceList>
            );
          })()}
          <FCList
            anchor={openWorkspaceClickButton?.current}
            placement="bottom-start"
            isOpen={workspaceListIsOpen}
            onClose={() => setWorkspaceListIsOpen(false)}
            noDivider
          >
            {workspaceTree.map(workspace => (
              <FCList.ListItem key={workspace?.id}>
                <TypographyEllipsis_Workspace
                  noWrap
                  component="p"
                  ref={workspaceRef}
                  variant="body"
                >
                  {workspace?.name}
                </TypographyEllipsis_Workspace>
              </FCList.ListItem>
            ))}
          </FCList>
        </>
      )}
      {currentProject && (
        <>
          <ChevronRight_Custom height={24} width={24} color={theme.palette.background.dark} />
          <BreadcrumbRow_Container
            entity={currentProject}
            intl={intl}
            publicId={`#${currentProject?.legacy_id}`}
            onClick={redirectToHome}
          />
        </>
      )}
    </Div_BreadcrumbContainer>
  );
};

Breadcrumb.propTypes = {
  intl: PropTypes.object.isRequired,
  openListOrganisation: PropTypes.func,
};

export default injectIntl(Breadcrumb);
